import type { MetaFunction } from 'react-router';
import type { GeoCoordinates, LocalBusiness, PostalAddress } from 'schema-dts';
import type { loader } from '~/modules/professional/page/loaders/professional-page-loader.server';
import type { StaticParentLoaders } from '../util';
import { getMetaBaseUrl } from '../util/create-meta-base-url';
import type { LibraryLocation } from '@archipro-website/top-navigation/generated/graphql';
import {
    buildGeo,
    cleanString,
    hasValidCoordinates,
} from '../util/professional-location-schema';

type AddressLocation = Pick<
    LibraryLocation,
    | 'Address1'
    | 'Address2'
    | 'Suburb'
    | 'City'
    | 'Region'
    | 'State'
    | 'Country'
    | 'PostCode'
    | 'Phone'
>;

export const createProfessionalSchema: MetaFunction<
    typeof loader,
    StaticParentLoaders
> = (props) => {
    const { data } = props;
    if (!data) return [];
    const { initial, localisation } = data;

    const baseUrl = getMetaBaseUrl(props);

    // if selectedAddress length is 0, add a default empty address to avoid Google complain
    const locations: AddressLocation[] = JSON.parse(
        JSON.stringify(initial.LibraryLocations),
    );
    if (!locations.length) {
        locations.push({
            Country: initial.Country,
        });
    }

    const selectedAddress: PostalAddress[] = locations.map((loc) => {
        return createPostalAddress(loc, initial.Country);
    });

    // Geo for the representative premises (Head Office, then Show Room, then first available
    // with coordinates) so it aligns with the primary address rather than an arbitrary site.
    const geo = buildGeo(selectGeoLocation(initial.LibraryLocations));

    let image =
        initial.Banners.edges?.[0]?.node.LibraryImageFile?.DetailBannerStandard
            .Desktop ?? initial.FacebookPreviewImage;
    if (image) {
        image = baseUrl + image;
    }

    const professionalSchema = constructProfessionalSchema({
        name: initial.Title,
        ...(localisation?.Description && {
            description: localisation.Description,
        }),
        url: baseUrl + initial.Link,
        ...(selectedAddress.length > 0 && { address: selectedAddress }),
        ...(geo && { geo }),
        logo: baseUrl + initial.CompanyLogo,
        ...(image && { image }),
    });

    return [professionalSchema];
};

/** Premises `Type` values preferred (in order) when picking the representative geo coordinate. */
const GEO_PREMISES_PRIORITY = ['Head Office', 'Showroom', 'Show Room'];

/**
 * Pick the map coordinates of the representative premises (Head Office, then Show Room, then the
 * first premises with coordinates) so `geo` aligns with the primary site rather than an arbitrary one.
 */
const selectGeoLocation = (
    locations?: Array<Pick<LibraryLocation, 'Type' | 'MapCoordinates'>>,
) => {
    const withCoords =
        locations?.filter((loc) => hasValidCoordinates(loc?.MapCoordinates)) ??
        [];
    if (!withCoords.length) return undefined;

    for (const type of GEO_PREMISES_PRIORITY) {
        const found = withCoords.find((loc) => loc.Type === type);
        if (found) return found.MapCoordinates;
    }

    return withCoords[0]?.MapCoordinates;
};

const createPostalAddress = (
    location: AddressLocation,
    defaultCountry?: string,
): PostalAddress => {
    const streetAddress = [location.Address1, location.Address2]
        .map(cleanString)
        .filter((value): value is string => !!value)
        .join(', ');

    const addressLocality =
        cleanString(location.Suburb) ?? cleanString(location.City);
    const addressCountry =
        cleanString(location.Country) ?? cleanString(defaultCountry);
    // Fall back to the coarser State when a premises has no Region (mirrors listing schema).
    const addressRegion =
        cleanString(location.Region) ?? cleanString(location.State);
    const postalCode = cleanString(location.PostCode);
    const telephone = cleanString(location.Phone);

    return {
        '@type': 'PostalAddress',
        ...(streetAddress && { streetAddress }),
        ...(addressLocality && { addressLocality }),
        ...(addressRegion && { addressRegion }),
        ...(postalCode && { postalCode }),
        ...(addressCountry && { addressCountry }),
        ...(telephone && { telephone }),
    };
};

interface ProfessionalSchemaProps {
    name: string;
    description?: string;
    url: string;
    image?: string;
    logo: string;
    address?: PostalAddress[];
    geo?: GeoCoordinates;
}

const constructProfessionalSchema = (
    prof: ProfessionalSchemaProps,
): Omit<LocalBusiness, 'string'> => {
    return {
        '@type': 'LocalBusiness',
        ...prof,
    };
};
